// TODO: move these to warbler (as the name implies)
import { percent } from '@perch-labs/warbler';
import jwtDecode from 'jwt-decode';
import { omit } from 'lodash';
import { isStorybook } from '~/utils/storybook';
import { IS_CYPRESS, IS_TEST } from './constants';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const never = (_param: never) => {};

// just a wrapper around lodash omit w/ better type inference
export const removeKeys = <TObject extends object>(obj: TObject, keys: (keyof TObject)[]) =>
  omit(obj, keys);

export const convertToPercentage = (float?: number): string | undefined => {
  if (!float) return undefined;
  return percent(float, 0) as string;
};

export const pluralize = (word: string, count: number) =>
  `${word}${count > 1 || count === 0 ? 's' : ''}`;

export const repeatArray = (array: any[], number: number) => Array(number).fill(array).flat();

type DecodedToken = {
  sub: string;
  exp: number;
};

export const decodeJwt = (token?: string) => {
  try {
    if (!token) throw new Error();
    const contents: DecodedToken = jwtDecode(token);
    const userId = contents.sub.split('User:')[1];
    if (!userId) {
      throw new Error('Token: userId');
    } else if (new Date(contents.exp * 1000) < new Date()) {
      throw new Error('Token: expired');
    } else {
      return { userId: Number(userId), valid: true };
    }
  } catch (e) {
    let reason = 'invalid';
    if (e.message === 'Token: userId') reason = 'userId';
    if (e.message === 'Token: expired') reason = 'expired';
    return { reason, valid: false };
  }
};

export const isBoolean = (value: unknown): value is Boolean => value === true || value === false;

export const getCookieValue = (name: string) =>
  document.cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`)?.pop() || '';

export const setCookie = (key: string, value: string | number, expiryDays: number) => {
  // this is to set new date with an expiry date from current date
  const expiryDate = new Date(new Date().setDate(new Date().getDate() + expiryDays));
  document.cookie = `${key}=${value};expires=${expiryDate.toUTCString()};`;
};

export const getUtmCookieValues = () => {
  const utmParams = {};
  const cookiePairs = document.cookie.match(/utm_[^;]+/g) || [];
  for (const cookiePair of cookiePairs) {
    const [cookieName, cookieValue] = cookiePair.split('=').map(str => {
      return str.trim();
    });
    if (cookieName.startsWith('utm_')) {
      utmParams[cookieName] = parseInt(cookieValue) || cookieValue;
    }
  }
  return utmParams;
};

// https://stackoverflow.com/questions/179355/clearing-all-cookies-with-javascript
export const deleteCookies = () =>
  document.cookie.split(';').forEach(function (c) {
    document.cookie = c
      .replace(/^ +/, '')
      .replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/`);
  });

export const hardRedirect = (path: string) => {
  if (isStorybook() || (IS_TEST && !IS_CYPRESS)) return;
  location.assign(`${location.origin}${path}`);
};

export const storageEnabled = () => {
  try {
    localStorage.setItem('storageTest', 'true');
    const value = localStorage.getItem('storageTest');
    localStorage.removeItem('storageTest');
    return !!value;
  } catch (e) {
    return false;
  }
};

/* eslint-disable no-console */
export const logging = (() => {
  const bindToConsole = (
    consoleMethod: (...args: any[]) => void,
    polyfill: (...args: any[]) => void,
  ) => {
    return consoleMethod ? consoleMethod.bind(console) : polyfill;
  };

  let prefix = '';

  const consoleLog = (...args: any[]) => {
    console.log(prefix, ...args);
  };

  const consoleError = (...args: any[]) => {
    console.error(prefix, ...args);
  };

  const consoleGroup = (...args: any[]) => {
    consoleLog(...args);
    prefix += '> ';
  };

  const consoleGroupEnd = () => {
    prefix = prefix.slice(0, -2);
  };

  return {
    error: consoleError,
    group: bindToConsole(console.group, consoleGroup),
    groupCollapsed: bindToConsole(console.groupCollapsed, consoleGroup),
    groupEnd: bindToConsole(console.groupEnd, consoleGroupEnd),
    log: consoleLog,
  };
})();
/* eslint-disable no-console */
